
import React, { useRef, useEffect, useState } from 'react';
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Rotate3D, ChevronDown } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import MathRenderer from "@/components/mock-test/MathRenderer";

interface FlashcardBackProps {
  content: string;
  onFlip: () => void;
}

const FlashcardBack: React.FC<FlashcardBackProps> = ({
  content,
  onFlip
}) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const hasContentOverflow = 
        contentRef.current.scrollHeight > contentRef.current.clientHeight;
      setHasOverflow(hasContentOverflow);
    }
  }, [content]);

  // Format content based on type
  const formatContent = (content: string) => {
    try {
      const parsedContent = JSON.parse(content);
      if (parsedContent && typeof parsedContent === 'object') {
        if (parsedContent.type === 'multiple-choice') {
          return (
            <div className="multiple-choice-content space-y-4">
              <p className="font-medium text-teal-700 dark:text-teal-300 mb-2">
                <MathRenderer text={parsedContent.question} />
              </p>
              <ul className="space-y-2 pl-5">
                {parsedContent.options.map((option: string, index: number) => (
                  <li 
                    key={index} 
                    className={`list-disc ${
                      index === parsedContent.correctIndex 
                        ? "text-green-600 dark:text-green-400 font-medium" 
                        : "text-slate-600 dark:text-slate-300"
                    }`}
                  >
                    <MathRenderer text={option} /> {index === parsedContent.correctIndex && "(✓)"}
                  </li>
                ))}
              </ul>
              {parsedContent.explanation && (
                <div className="mt-4 pt-3 border-t border-teal-100 dark:border-teal-800">
                  <p className="text-sm font-medium text-teal-700 dark:text-teal-300">Explanation:</p>
                  <p className="text-sm text-teal-600 dark:text-teal-400">
                    <MathRenderer text={parsedContent.explanation} />
                  </p>
                </div>
              )}
            </div>
          );
        }
      }
    } catch (e) {
      // Not JSON, treat as regular text or markdown
    }

    // Check if this is an AI-generated explanation with the standard format
    if (content.includes("Understanding the ") || content.includes("Correct Answer:") || 
        content.includes("Why Other Options are Incorrect")) {
      
      // Format AI explanation with better styling
      const sections = content.split(/\n\n+/);
      
      return (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {/* Title */}
          {content.includes("Understanding the ") && (
            <h3 className="text-lg font-bold text-teal-700 dark:text-teal-300 border-b border-teal-200 dark:border-teal-800 pb-1 mb-3">
              {content.match(/Understanding the [^:\n]+/)?.[0] || "Explanation"}
            </h3>
          )}
          
          {/* Main Content with sections */}
          <div className="whitespace-pre-line text-teal-700 dark:text-teal-200 leading-relaxed">
            {sections.map((section, idx) => {
              // Skip empty sections
              if (!section.trim()) return null;
              
              // Format correct answer section
              if (section.includes("Correct Answer:")) {
                return (
                  <div key={idx} className="mb-3 p-2 bg-teal-50 dark:bg-teal-900/30 rounded-md border-l-4 border-teal-400 dark:border-teal-600">
                    <p className="font-medium">
                      <MathRenderer text={section} />
                    </p>
                  </div>
                );
              }
              
              // Format section headings
              if (section.includes("Why Other Options are Incorrect") || 
                  section.includes("Additional Context") ||
                  section.match(/^#+\s/)) {
                return (
                  <h4 key={idx} className="font-semibold text-teal-600 dark:text-teal-300 mt-4 mb-2">
                    <MathRenderer text={section.replace(/^#+\s/, '')} />
                  </h4>
                );
              }
              
              // Format numbered options (1. Option - explanation)
              if (section.match(/^\d+\.\s+/)) {
                const optionMatch = section.match(/^(\d+)\.\s+(.+?)(\s+-\s+|\:\s+)(.+)/s);
                if (optionMatch) {
                  const [_, number, optionText, separator, explanation] = optionMatch;
                  return (
                    <div key={idx} className="mb-3">
                      <div className="font-medium flex">
                        <span className="mr-1.5">{number}.</span>
                        <span className="font-medium">
                          <MathRenderer text={optionText} />
                        </span>
                      </div>
                      <div className="ml-5 pl-2 border-l-2 border-teal-200 dark:border-teal-700 text-teal-600 dark:text-teal-400 text-sm mt-1">
                        <MathRenderer text={explanation} />
                      </div>
                    </div>
                  );
                }
              }
              
              // Default paragraph formatting with special handling for bold text
              return (
                <p key={idx} className="mb-3 last:mb-0">
                  {section.split(/\*\*([^*]+)\*\*/).map((part, i) => 
                    i % 2 === 0 ? <MathRenderer key={i} text={part} /> : 
                    <strong key={i} className="text-teal-700 dark:text-teal-300">
                      <MathRenderer text={part} />
                    </strong>
                  )}
                </p>
              );
            })}
          </div>
        </div>
      );
    }

    // Default formatting for regular text
    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <div className="whitespace-pre-line text-teal-700 dark:text-teal-200 leading-relaxed">
          {content.split('\n').map((paragraph, idx) => (
            paragraph.trim() && (
              <p key={idx} className="mb-3 last:mb-0">
                <MathRenderer text={paragraph.trim()} />
              </p>
            )
          ))}
        </div>
      </div>
    );
  };

  return (
    <CardContent className="h-full flex flex-col p-4 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30">
      <ScrollArea className="flex-1 pr-2 overflow-y-auto">
        <div 
          ref={contentRef}
          className="max-w-full pr-1"
        >
          {formatContent(content)}
        </div>
      </ScrollArea>
      
      <div className="mt-auto pt-2 flex justify-center">
        <Button 
          variant="ghost" 
          size="sm"
          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:text-teal-400 dark:hover:text-teal-300 dark:hover:bg-teal-900/30"
          onClick={(e) => {
            e.stopPropagation();
            onFlip();
          }}
        >
          <Rotate3D className="h-4 w-4 mr-2" />
          Flip Card
        </Button>
      </div>
      
      {hasOverflow && (
        <div className="text-center text-xs text-teal-500 dark:text-teal-400 flex items-center justify-center mt-1">
          <ChevronDown className="h-3 w-3" />
          <span className="ml-1">Scroll for more</span>
        </div>
      )}
    </CardContent>
  );
};

export default FlashcardBack;
